import React, { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Share2, Download, Info, ChevronDown, Search, X, Landmark  } from "lucide-react";
import QRCode from "react-qr-code";
import { toast } from "react-toastify";
import { useReferralCode } from "../hooks/useReferralCode";
import { useReferredWorkers } from "../hooks/useReferredWorkers";
import headerlogo from "../assets/headerLogo.svg"; 
import BankDetailsCard from "../components/BankDetailsCard";

const PAGE_SIZE = 10;
const ALL = "all";
const BRAND = "#5D45F9";
const FONT_STACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const LOGO_H = 30;
const LOGO_TOP = 20;
const LOGO_SHIFT = 38; 

const getMonthName = (num) => (num === ALL ? "All" : MONTH_NAMES[Number(num) - 1]);

const NOW = new Date();
const CURRENT_YEAR = String(NOW.getFullYear());
const CURRENT_MONTH = NOW.getMonth() + 1; // 1-12

const YEAR_COUNT = 6;
const buildYearOptions = () => {
  const y = new Date().getFullYear();
  return Array.from({ length: YEAR_COUNT }, (_, i) => String(y - i));
};

const roundedRectPath = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const fitText = (ctx, text, maxWidth) => {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxWidth) {
    t = t.slice(0, -1);
  }
  return `${t.trimEnd()}…`;
};

// Load an image from a URL / blob URL
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const SuperAdminReferAndEarnContent = ({ token, userId, workerType, user, userName }) => {
  const nameToDisplay = userName || user?.name || "User";

  const {
    loading: codeLoading,
    error: codeError,
    referralCode,
    referrerName,
    amount,
    referralLink,
    howItWorks,
  } = useReferralCode({ token, workerType });

  const yearOptions = useMemo(buildYearOptions, []);
  const [selectedYear, setSelectedYear] = useState(ALL);
  const [selectedMonthNum, setSelectedMonthNum] = useState(ALL); 
  const [showBankDetails, setShowBankDetails] = useState(false);

  const maxMonth =
    selectedYear === ALL || selectedYear === CURRENT_YEAR ? CURRENT_MONTH : 12;

  const monthParam =
    selectedYear !== ALL && selectedMonthNum !== ALL ? `${selectedYear}-${selectedMonthNum}` : undefined;
  const yearParam = selectedYear !== ALL && selectedMonthNum === ALL ? selectedYear : undefined;

  const handleMonthChange = (e) => {
    const m = e.target.value;
    setSelectedMonthNum(m);
    if (m === ALL) setSelectedYear(ALL);
    else if (selectedYear === ALL) setSelectedYear(CURRENT_YEAR);
  };

  const handleYearChange = (e) => {
    const y = e.target.value;
    setSelectedYear(y);
    if (y === ALL) {
      setSelectedMonthNum(ALL); 
    } else if (y === CURRENT_YEAR && selectedMonthNum !== ALL && Number(selectedMonthNum) > CURRENT_MONTH) {
      setSelectedMonthNum(ALL); 
    }
  };
  const [searchInput, setSearchInput] = useState("");
  const [searchName, setSearchName] = useState(""); 
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const qrFileRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchName(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Overall totals (no month filter) -> Summary card
  const overall = useReferredWorkers({ token, userId, workerType });

  const monthData = useReferredWorkers({
    token,
    userId,
    workerType,
    // While searching by name, ignore the month/year filter (search across everything)
    month: searchName ? undefined : monthParam,
    year: searchName ? undefined : yearParam,
    name: searchName || undefined,
  });

  const { allReferrals, initialLoading: listLoading, error: listError } = monthData;

  const monthReferralCount = monthData.totalReferrals ?? allReferrals.length;
  const monthEarnings =
    monthData.totalEarning ??
    allReferrals.reduce((sum, r) => sum + (Number(r.earning) || 0), 0);

  const overallReferrals = overall.totalReferrals ?? overall.allReferrals.length;
  const overallEarnings =
    overall.totalEarning ??
    overall.allReferrals.reduce((sum, r) => sum + (Number(r.earning) || 0), 0);

  const visible = allReferrals.slice(0, visibleCount);
  const hasMore = visibleCount < allReferrals.length;

  const isFiltered = selectedYear !== ALL && !searchName;
  const periodLabel =
    selectedMonthNum !== ALL
      ? `${getMonthName(selectedMonthNum)} ${selectedYear}`
      : selectedYear;

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [selectedYear, selectedMonthNum, searchName]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setVisibleCount((c) => c + PAGE_SIZE);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, visibleCount, listLoading]);

  const handleCopyCode = async () => {
    if (!referralCode) return;
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy code");
    }
  };

  const renderCardCanvas = async () => {
    const svg = document.getElementById("superadmin-referral-qr-svg");
    if (!svg) throw new Error("QR not found");

    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const qrUrl = URL.createObjectURL(svgBlob);

    try {
      // Load the QR and the Factrika logo together
      const [img, logoImg] = await Promise.all([loadImage(qrUrl), loadImage(headerlogo)]);

      const W = 340;
      const H = (referrerName ? 412 : 380) + LOGO_SHIFT;
      const SCALE = 3; // high-res output

      const canvas = document.createElement("canvas");
      canvas.width = W * SCALE;
      canvas.height = H * SCALE;
      const ctx = canvas.getContext("2d");
      ctx.scale(SCALE, SCALE);

      // Card background
      roundedRectPath(ctx, 0, 0, W, H, 20);
      ctx.fillStyle = "#EEF0FF";
      ctx.fill();

      const ratio =
        logoImg.naturalWidth && logoImg.naturalHeight
          ? logoImg.naturalWidth / logoImg.naturalHeight
          : 4;
      let logoH = LOGO_H;
      let logoW = logoH * ratio;
      const maxLogoW = W - 80;
      if (logoW > maxLogoW) {
        logoW = maxLogoW;
        logoH = logoW / ratio;
      }
      ctx.drawImage(logoImg, (W - logoW) / 2, LOGO_TOP + (LOGO_H - logoH) / 2, logoW, logoH);

      // Title
      ctx.fillStyle = "#374151";
      ctx.font = `600 16px ${FONT_STACK}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.fillText("Scan Referral QR", W / 2, 42 + LOGO_SHIFT);

      // White box behind QR
      const boxSize = 252;
      const boxX = (W - boxSize) / 2;
      const boxY = 62 + LOGO_SHIFT;
      roundedRectPath(ctx, boxX, boxY, boxSize, boxSize, 12);
      ctx.fillStyle = "#FFFFFF";
      ctx.fill();

      // QR code
      const qrSize = 228;
      ctx.drawImage(img, boxX + (boxSize - qrSize) / 2, boxY + (boxSize - qrSize) / 2, qrSize, qrSize);

      let cursorY = boxY + boxSize + 34;

      // Referral name
      if (referrerName) {
        ctx.fillStyle = "#111827";
        ctx.font = `700 17px ${FONT_STACK}`;
        ctx.textAlign = "center";
        ctx.fillText(fitText(ctx, referrerName, W - 40), W / 2, cursorY);
        cursorY += 32;
      }

      // Footer: "Referral code :  <code>" — centered by measured width
      const label = "Referral code :";
      const labelFont = `500 15px ${FONT_STACK}`;
      const codeFont = `700 16px ${FONT_STACK}`;
      const gap = 8;

      ctx.textAlign = "left";
      ctx.font = labelFont;
      const labelW = ctx.measureText(label).width;
      ctx.font = codeFont;
      const codeW = ctx.measureText(referralCode).width;

      const startX = (W - (labelW + gap + codeW)) / 2;

      ctx.fillStyle = "#374151";
      ctx.font = labelFont;
      ctx.fillText(label, startX, cursorY);

      ctx.fillStyle = BRAND;
      ctx.font = codeFont;
      ctx.fillText(referralCode, startX + labelW + gap, cursorY);

      return canvas;
    } finally {
      URL.revokeObjectURL(qrUrl);
    }
  };

  // Canvas → PNG File (needed for sharing the QR image)
  const canvasToFile = (canvas) =>
    new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("Couldn't create image"));
        resolve(new File([blob], `factrika-referral-${referralCode}.png`, { type: "image/png" }));
      }, "image/png");
    });

  useEffect(() => {
    if (codeLoading || !referralCode || !referralLink) return;
    let cancelled = false;
    qrFileRef.current = null;
    (async () => {
      try {
        const file = await canvasToFile(await renderCardCanvas());
        if (!cancelled) qrFileRef.current = file;
      } catch {
        // falls back to building on demand when the user taps share
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeLoading, referralCode, referralLink, referrerName]);

  const handleShare = async () => {
    if (!referralCode) return;

    const baseText = [
      "Join me on Factrika and start earning! 💼",
      "",
      `Use my referral code: ${referralCode}`,
      `Or sign up using this link: ${referralLink}`,
    ].join("\n");
    const textWithQr = `${baseText}\n\nYou can also scan the attached QR to get started.`;

    try {
      let file = qrFileRef.current;
      if (!file) {
        file = await canvasToFile(await renderCardCanvas()).catch(() => null);
      }

      // 1) Message + QR image (WhatsApp, Telegram, etc.)
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: "Join Factrika", text: textWithQr, files: [file] });
        return;
      }

      if (navigator.share) {
        await navigator.share({ title: "Join Factrika", text: baseText });
        return;
      }

      // 3) Desktop fallback — copy the message
      await navigator.clipboard.writeText(baseText);
      toast.success("Referral message copied — share it with your friends!");
    } catch (err) {
      if (err && err.name === "AbortError") return; 
      try {
        await navigator.clipboard.writeText(baseText);
        toast.success("Referral message copied — share it with your friends!");
      } catch {
        toast.error("Couldn't share referral");
      }
    }
  };

  // Downloads the full card as a PNG
  const handleDownloadQR = async () => {
    if (!referralCode || downloading) return;
    setDownloading(true);
    try {
      const canvas = await renderCardCanvas();
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `factrika-referral-${referralCode}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Failed to download QR:", err);
      toast.error("Couldn't download QR");
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d)) return "—";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  if (codeLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-3 border-gray-200 border-t-3 border-t-[#5D45F9] rounded-full animate-spin" />
      </div>
    );
  }

  if (codeError || !referralCode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500 text-sm">{codeError || "Couldn't load your referral code"}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-sm p-8">
        <h1 className="text-2xl font-bold text-[#1E2022] text-center mb-8">Refer &amp; Earn</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch mb-8">
          {/* Left Main Card */}
          <div className="bg-[#F6FBFF] border border-[#EAF2FF] rounded-2xl p-6 flex flex-col justify-between gap-4 h-full">
            <div>
              <h2 className="text-2xl font-extrabold text-[#111827] mb-2 tracking-tight">
                Refer workers and <span className="text-[#5D45F9]">{amount || "Get ₹100"}</span>
              </h2>
              <p className="text-xs text-gray-600 max-w-lg leading-relaxed">
                As a supervisor, refer workers to Factrika. When your referred worker completes 10 shifts, you get ₹100.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-5 items-end">
              {/* QR Code Container */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div className="p-0.5">
                  <QRCode id="superadmin-referral-qr-svg" value={referralLink} size={128} />
                </div>
                <p className="text-xs font-bold text-gray-800 mt-2 text-center">Scan Referral QR</p>
              </div>

              {/* Action Column */}
              <div className="flex-1 w-full">
                <p className="text-xs font-bold text-gray-500 mb-1.5">Referral Code</p>

                {/* Referral Code Box */}
                <div className="relative mb-3">
                  <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-3.5 py-2.5 shadow-sm">
                    <span className="font-bold text-gray-900 text-sm">{referralCode}</span>
                    <button
                      onClick={handleCopyCode}
                      className="relative p-1 hover:bg-gray-50 rounded transition-colors"
                      title="Copy referral code"
                    >
                      <Copy className="w-4 h-4 text-[#5D45F9]" />
                    </button>
                  </div>
                  {copied && (
                    <span className="absolute -top-7 right-0 bg-gray-900 text-white text-[10px] font-semibold px-2 py-0.5 rounded shadow-md">
                      Copied!
                    </span>
                  )}
                </div>

                {/* Buttons */}
                <div className="flex gap-2.5">
                  <button
                    onClick={handleShare}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#5D45F9] hover:bg-[#4c35e0] text-white text-xs font-bold py-2.5 px-2 rounded-xl transition-all shadow-sm whitespace-nowrap"
                  >
                    <Share2 className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Share with Worker</span>
                  </button>
                  <button
                    onClick={handleDownloadQR}
                    disabled={downloading}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white border border-[#5D45F9] text-[#5D45F9] hover:bg-indigo-50/50 disabled:opacity-70 text-xs font-bold py-2.5 px-2 rounded-xl transition-all shadow-sm whitespace-nowrap"
                  >
                    <Download className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Download QR</span>
                  </button>
                </div>
                <button
  onClick={() => setShowBankDetails(true)}
  className="w-full mt-2.5 inline-flex items-center justify-center gap-1.5 bg-white border border-[#5D45F9] text-[#5D45F9] hover:bg-indigo-50/50 text-xs font-bold py-2.5 px-2 rounded-xl transition-all shadow-sm whitespace-nowrap"
>
  <Landmark className="w-3.5 h-3.5 flex-shrink-0" />
  <span>Bank Details</span>
</button>
              </div>
            </div>
          </div>

          {/* Right Column — How it works + Summary */}
          <div className="flex flex-col gap-4 h-full justify-between">
            {/* How it works Card */}
            <div className="bg-[#F6FBFF] border border-[#EAF2FF] rounded-2xl p-5 flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-3.5">
                <Info className="w-4 h-4 text-[#5D45F9]" />
                <p className="text-[#5D45F9] font-bold text-sm">How it works</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3.5">
                {howItWorks.map((step, index) => (
                  <div key={`${step.title}-${index}`} className="flex gap-2.5 items-start">
                    <div className="w-7 h-7 flex-shrink-0 rounded-full bg-[#EEF2FF] text-[#5D45F9] font-bold flex items-center justify-center text-xs">
                      {index + 1}
                    </div>
                    <div>
                      {step.title && <p className="font-bold text-gray-900 text-xs mb-0.5">{step.title}</p>}
                      {step.description && (
                        <p className="text-gray-500 text-[11px] leading-relaxed">{step.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Summary Card */}
            <div className="bg-[#F6FBFF] border border-[#EAF2FF] rounded-2xl p-4">
              <p className="text-[#5D45F9] font-bold text-sm mb-2.5">Summary</p>
              <div className="flex gap-8 items-center">
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-semibold text-gray-600">Total Referrals :</p>
                  <p className="font-extrabold text-[#5D45F9] text-base">
                    {overall.initialLoading ? "—" : overallReferrals}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-semibold text-gray-600">Total Earnings :</p>
                  <p className="font-extrabold text-[#5D45F9] text-base">
                    {overall.initialLoading ? "—" : `₹ ${overallEarnings}`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* My Referrals Table */}
        <div>
          {/* Search by name */}
          <div className="relative mb-4 max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name"
              aria-label="Search referrals by name"
              className="w-full bg-white text-gray-900 placeholder-gray-400 text-sm rounded-xl border border-gray-200 pl-9 pr-8 py-2 focus:outline-none focus:border-[#5D45F9] focus:ring-1 focus:ring-[#5D45F9] transition-colors"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"
              >
                <X className="w-3.5 h-3.5 text-gray-500" />
              </button>
            )}
          </div>

          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <h3 className="font-bold text-[#111827] text-base">My Referrals</h3>

              {/* Month filter */}
              <div className="relative inline-block">
                <select
                  value={selectedMonthNum}
                  onChange={handleMonthChange}
                  disabled={Boolean(searchName)}
                  aria-label="Filter by month"
                  className="appearance-none bg-[#5D45F9] hover:bg-[#4c35e0] text-white text-xs font-medium rounded-full pl-4 pr-8 py-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none transition-colors shadow-sm"
                >
                  <option value={ALL} className="bg-white text-gray-900">All months</option>
                  {MONTH_NAMES.slice(0, maxMonth).map((name, i) => (
                    <option key={name} value={String(i + 1).padStart(2, "0")} className="bg-white text-gray-900">
                      {name}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-white">
                  <ChevronDown className="w-3.5 h-3.5 fill-current" />
                </span>
              </div>

              {/* Year filter */}
              <div className="relative inline-block">
                <select
                  value={selectedYear}
                  onChange={handleYearChange}
                  disabled={Boolean(searchName)}
                  aria-label="Filter by year"
                  className="appearance-none bg-[#5D45F9] hover:bg-[#4c35e0] text-white text-xs font-medium rounded-full pl-4 pr-8 py-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none transition-colors shadow-sm"
                >
                  <option value={ALL} className="bg-white text-gray-900">All years</option>
                  {yearOptions.map((y) => (
                    <option key={y} value={y} className="bg-white text-gray-900">
                      {y}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-white">
                  <ChevronDown className="w-3.5 h-3.5 fill-current" />
                </span>
              </div>
            </div>

            {/* Dynamic Referrals & Earnings Header */}
            <div className="flex items-center gap-6 text-sm font-semibold text-[#111827]">
              <span>
                {isFiltered ? `${periodLabel} Referrals` : "Total Referrals"} :{" "}
                {listLoading ? "—" : monthReferralCount}
              </span>
              <span>
                {isFiltered ? `Earnings in ${periodLabel}` : "Total Earnings"} :{" "}
                {listLoading ? "—" : `₹${monthEarnings}`}
              </span>
            </div>
          </div>

          {listLoading && (
            <div className="flex justify-center py-6">
              <div className="w-6 h-6 border-2 border-gray-200 border-t-2 border-t-[#5D45F9] rounded-full animate-spin" />
            </div>
          )}

          {listError && <p className="text-center text-red-600 text-sm py-4">{listError}</p>}

          {!listLoading && !listError && allReferrals.length === 0 && (
            <p className="text-center text-gray-400 text-sm py-8">
              {searchName
                ? `No referrals found for "${searchName}".`
                : !isFiltered
                ? "No referrals yet."
                : "No referrals for this period."}
            </p>
          )}

          {!listLoading && !listError && allReferrals.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="py-2 font-medium">Name</th>
                    <th className="py-2 font-medium">Phone Number</th>
                    <th className="py-2 font-medium">Joined at</th>
                    <th className="py-2 font-medium">Shift Completed</th>
                    <th className="py-2 font-medium">Earnings</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((ref, index) => (
                    <tr key={ref._id || index} className="border-b border-gray-50">
                      <td className="py-3 font-semibold text-gray-800">
                        {index + 1}. {ref.name || "—"}
                      </td>
                      <td className="py-3 text-gray-600">{ref.phone || "—"}</td>
                      <td className="py-3 text-gray-600">{formatDate(ref.onBoardingDate)}</td>
                      <td className="py-3 text-gray-600">{ref.shift ?? "—"}</td>
                      <td className="py-3 font-semibold text-[#5D45F9]">
                        {ref.earning ? `₹${ref.earning}` : "—"}
                      </td>
                      <td className="py-3 text-gray-600">{ref.referralStatus || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Sentinel for infinite scroll */}
              <div ref={sentinelRef} className="min-h-[20px]">
                {!hasMore && (
                  <p className="text-center text-gray-400 text-xs my-2">You've seen all referrals</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <BankDetailsCard
  isOpen={showBankDetails}
  onClose={() => setShowBankDetails(false)}
/>
    </div>
  );
};

export default SuperAdminReferAndEarnContent;